// What a stored SOA Item says about its signatures (deferred countersigning,
// 2026-09-30). A renewal or AEP-survey SOA is stored as the client signed it
// (`provider_sign_status: 'awaiting_agent'`) and countersigned when the AEP
// recommendation is sent, by whoever sends it; every other SOA is countersigned
// in the same request. The e-SOA page, the agent's SOA list and the summary
// page's guard read this one helper so "signed" means the same thing on each.
//
// Legacy pretty-print SOAs (no Documenso: `provider` unset, agent stamped) are
// signed by both sides with no PDF; a 'error' status is a countersignature that
// never filed, which the summary page lets the agent retry.
export function soaSigningState (soa) {
    const value = (soa && soa.value) || null
    if (!value) {
        return { exists: false, awaitingAgent: false, agentSigned: false, failed: false, pdfReady: false, pdfPending: false }
    }
    const status = value.provider_sign_status || null
    const awaitingAgent = status === 'awaiting_agent'
    const failed = status === 'error'
    const pdfReady = !!value.envelope_item_id
    return {
        exists: true,
        awaitingAgent,
        agentSigned: !!value.agent_sign_date && !awaitingAgent && !failed,
        failed,
        pdfReady,
        pdfPending: value.provider === 'documenso' && !pdfReady && status === 'pending',
    }
}

// Refetch the client's recent SOA (the store's `soa60days`) after something
// changed it server-side -- the summary send countersigns an awaiting one --
// so the summary page's guard stops saying "awaiting" without a reload.
// `vm` is a component with `client`, `$api` and `$store`.
export async function refreshRecentSoa (vm) {
    const soa = vm.$store.getters.soa60days
    if (!soa || !soa.key || !vm.client || !vm.client.id) { return }
    const fresh = await vm.$api.getSignedSoa(vm.client.id, soa.key)
    if (!fresh || fresh.error) { return }
    vm.$store.commit('soa60days', fresh)
}

// The two fields the advisor fills at countersign time, as display labels for
// the pretty-print SOA page (ESoa.vue): null means "not filled yet", which the
// page says out loud so an awaiting SOA never looks complete. `moment` is the
// app's $moment.
export function soaCountersignLabels (soa, moment) {
    const value = (soa && soa.value) || {}
    const day = (v) => {
        if (!v) { return null }
        const m = moment(v)
        return m.isValid() ? m.format('MMMM D, YYYY') : null
    }
    const state = soaSigningState(soa)
    let agentSignature = null
    if (state.failed) {
        agentSignature = 'did not file (Documenso error)'
    } else if (value.agent_sign_date) {
        agentSignature = `${value.agent_name || 'Advisor'} on ${day(value.agent_sign_date)}`
    }
    return { appointmentDate: day(value.appointment_date), agentSignature }
}

// The client's answers on a stored SOA (an Item or its value), shaped like the
// pretty-print form's (ESoa.vue): a SOA on file shows what the client ticked,
// never the form's all-ticked defaults. null when there is nothing to show.
export function soaFormAnswers (soa) {
    const value = (soa && soa.value) || soa
    if (!value || !value.products) { return null }
    const p = value.products
    return {
        products: { mapd: !!p.mapd, pdp: !!p.pdp, supp: !!p.supp, dental_vision_hearing: !!p.dental_vision_hearing },
        role: value.role || 'beneficiary',
    }
}

// "Products to discuss" on a stored SOA, as short labels for the agent's SOA
// list (SignedSoas.vue), in the form's order: [] when the client ticked none,
// null when the record has no products at all (nothing to say).
const PRODUCT_LABELS = [
    ['mapd', 'Medicare Advantage (Part C)'],
    ['pdp', 'Prescription Drug (Part D)'],
    ['supp', 'Medicare Supplement (Medigap)'],
    ['dental_vision_hearing', 'Dental, Vision & Hearing'],
]

export function soaProductLabels (soa) {
    const answers = soaFormAnswers(soa)
    if (!answers) { return null }
    return PRODUCT_LABELS.filter(([key]) => answers.products[key]).map(([, label]) => label)
}

// Countersign a client's SOA as the calling agent (flow/consults/endpoints/
// survey.py) -- the summary page's guard, the single-SOA page and the agent's
// SOA list all sign through here. The store's `soa60days` (the client's latest,
// which backs the guard and the client's sign step) is refreshed only when it
// is the SOA just signed: the single-SOA page and the list can sign an older
// one. Returns the signed SOA, or null when the call failed ($try has said why).
// `vm` is a component with `$try`, `$store` and `$success`.
export async function countersignSoa (vm, clientId, soa) {
    const resp = await vm.$try('countersignSoa', clientId, soa.key)
    if (!resp || resp.error) { return null }
    const latest = vm.$store.getters.soa60days
    if (latest && latest.key === soa.key) {
        vm.$store.commit('soa60days', resp)
    }
    vm.$success('SOA countersigned. The PDF is being prepared.')
    return resp
}
